import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
    Box, Typography, Container, Alert,
    Chip, Button, Avatar, Grid, Paper,
    Stack, CircularProgress, Fade, Zoom,
    IconButton, Tooltip, useTheme
} from '@mui/material';
import {
    Description as DescriptionIcon,
    CalendarToday as CalendarIcon,
    Event as EventIcon,
    Schedule as ScheduleIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    History as HistoryIcon,
    School as SchoolIcon,
    Info as InfoIcon,
    Timer as TimerIcon
} from '@mui/icons-material';
import { formatDatePart, getCurrentBSDate } from '../../../utils/customDate';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 45px -12px rgba(0, 0, 0, 0.1) !important;
    }
`;

const StatCard = styled(Paper)`
    padding: 24px;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const StudentExamRoutine = () => {
    const { currentUser } = useSelector((state) => state.user);
    const [examRoutines, setExamRoutines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const classData = currentUser?.sclassName || currentUser?.sclass;
        if (currentUser?._id && classData) {
            const classId = typeof classData === 'object' ? classData._id : classData;
            const schoolId = currentUser.school?._id || currentUser.school;
            if (schoolId && classId) {
                fetchExamRoutines(schoolId, classId);
            }
        } else {
            setLoading(false);
        }
    }, [currentUser]);

    const fetchExamRoutines = async (schoolId, classId) => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/ExamRoutine/Student/${schoolId}/${classId}`
            );
            if (Array.isArray(result.data)) {
                setExamRoutines(result.data);
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Error loading exam routines' });
        }
        setLoading(false);
    };

    const getExamTypeInfo = (type) => {
        const types = {
            first: { label: 'First Terminal', color: '#6366f1', bg: '#eef2ff' },
            second: { label: 'Second Terminal', color: '#8b5cf6', bg: '#f5f3ff' },
            mid: { label: 'Mid Term', color: '#10b981', bg: '#ecfdf5' },
            preboard: { label: 'Pre-Board', color: '#f59e0b', bg: '#fff7ed' },
            final: { label: 'Final Examination', color: '#ef4444', bg: '#fef2f2' }
        };
        return types[type] || { label: type || 'General Exam', color: '#64748b', bg: '#f1f5f9' };
    };

    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        const filename = filePath.split(/[/\\]/).pop();
        return `${process.env.REACT_APP_BASE_URL}/uploads/exam-routines/${encodeURIComponent(filename)}`;
    };

    const handleDownload = (routine) => {
        const fullUrl = getFileUrl(routine.filePath);
        if (fullUrl) {
            window.open(fullUrl, '_blank');
        } else {
            alert('Routine file not available.');
        }
    };

    const { upcoming, past } = useMemo(() => {
        const today = getCurrentBSDate();
        return {
            upcoming: examRoutines.filter(r => !r.examDate || formatDatePart(r.examDate) >= today)
                .sort((a, b) => (formatDatePart(a.examDate) || '').localeCompare(formatDatePart(b.examDate) || '')),
            past: examRoutines.filter(r => r.examDate && formatDatePart(r.examDate) < today)
                .sort((a, b) => (formatDatePart(b.examDate) || '').localeCompare(formatDatePart(a.examDate) || ''))
        };
    }, [examRoutines]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={50} thickness={4} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="xl">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <EventIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Exam Schedules
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Access your examination routines and localized schedules.
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right', bgcolor: 'rgba(255,255,255,0.1)', p: 2, borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 0.5 }}>Upcoming Exams</Typography>
                                <Typography variant="h3" sx={{ fontWeight: 900 }}>{upcoming.length}</Typography>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
                {message.text && (
                    <Alert severity={message.type} sx={{ mb: 4, borderRadius: '16px' }} onClose={() => setMessage({ type: '', text: '' })}>
                        {message.text}
                    </Alert>
                )}

                {examRoutines.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 12 }}>
                        <Avatar sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', width: 100, height: 100, mx: 'auto', mb: 3 }}>
                            <EventIcon sx={{ fontSize: 50 }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>No Exam Routines Yet</Typography>
                        <Typography variant="body1" sx={{ color: '#64748b', mt: 1 }}>
                            Your examination schedules will appear here once published by the administration.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {/* Summary Stats */}
                        <Grid item xs={12} md={4}>
                            <Stack spacing={3}>
                                <StatCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1' }}><TimerIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PENDING EXAMS</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{upcoming.length} Sessions</Typography>
                                        </Box>
                                    </Box>
                                </StatCard>
                                <StatCard elevation={0}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ bgcolor: '#f5f3ff', color: '#8b5cf6' }}><HistoryIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>PAST ROUTINES</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{past.length} Archived</Typography>
                                        </Box>
                                    </Box>
                                </StatCard>
                                <Paper sx={{ p: 3, borderRadius: '24px', bgcolor: '#1e293b', color: 'white' }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <SchoolIcon sx={{ color: '#6366f1' }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Class Level</Typography>
                                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                                {currentUser?.sclassName?.sclassName || 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Grid>

                        {/* Routines List */}
                        <Grid item xs={12} md={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6' }}><ScheduleIcon /></Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>Schedule & Timelines</Typography>
                            </Box>
                            
                            <Stack spacing={3}>
                                {upcoming.map((routine, index) => {
                                    const typeInfo = getExamTypeInfo(routine.examType);
                                    return (
                                        <Zoom in timeout={400 + index * 100} key={routine._id}>
                                            <GlassCard sx={{ p: 0 }}>
                                                <Grid container>
                                                    <Grid item xs={12} sm={3} sx={{ bgcolor: typeInfo.bg, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid rgba(0,0,0,0.05)' }}>
                                                        <CalendarIcon sx={{ color: typeInfo.color, fontSize: 32, mb: 1 }} />
                                                        <Typography variant="h6" sx={{ fontWeight: 900, color: typeInfo.color }}>
                                                            {formatDatePart(routine.examDate)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={9} sx={{ p: 3 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                            <Box>
                                                                <Chip label={typeInfo.label} size="small" sx={{ bgcolor: typeInfo.bg, color: typeInfo.color, fontWeight: 800, mb: 1, borderRadius: '8px' }} />
                                                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                                                    {routine.title || routine.name}
                                                                </Typography>
                                                            </Box>
                                                            <Stack direction="row" spacing={1}>
                                                                <Tooltip title="View PDF">
                                                                    <IconButton onClick={() => handleDownload(routine)} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                                                                        <VisibilityIcon sx={{ color: '#64748b' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Button 
                                                                    variant="contained" 
                                                                    startIcon={<DownloadIcon />}
                                                                    onClick={() => handleDownload(routine)}
                                                                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </Stack>
                                                        </Box>
                                                        <Stack direction="row" spacing={3}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <CalendarIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                    {formatDatePart(routine.examDate)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <DescriptionIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                                                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                    PDF Document
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </GlassCard>
                                        </Zoom>
                                    );
                                })}

                                {past.length > 0 && (
                                    <>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#64748b', mt: 4, mb: 1 }}>
                                            Archived Schedules
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {past.map((routine) => (
                                                <Grid item xs={12} key={routine._id}>
                                                    <Paper sx={{ p: 2, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', width: 40, height: 40 }}><HistoryIcon fontSize="small" /></Avatar>
                                                            <Box>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569' }}>{routine.title || routine.name}</Typography>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>Ended: {formatDatePart(routine.examDate)}</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Button size="small" onClick={() => handleDownload(routine)} sx={{ fontWeight: 700 }}>View Archive</Button>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                )}
            </ContentContainer>
        </PageWrapper>
    );
};

export default StudentExamRoutine;
